import { FC, Props } from "./typings";
import styles from "./styles.module.scss";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import useGridRowSelection from "hooks/useGridRowSelection";
import { useInfiniteQuery } from "react-query";
import { axiosInstance } from "api/axios";
import Search from "assets/icons/search.gif";
import {
  DataGridPremium,
  GridRenderCellParams,
} from "@mui/x-data-grid-premium";
import moment from "moment";
import NameCell from "./Cells/NameCell";
import StatusCell from "./Cells/StatusCell";
import statusService from "api/statusService";
import MenuCell from "./Cells/MenuCell";
import { plGridLocales } from "utils/muiGridLocales";
import AddressBookVerificationsContext from "contexts/AddressBookVerificationsContext";
import { IAddressBookVerificationListItem } from "dto/Admin/IAddressBookVerification";
import AddAttachmentModal from "./Modals/AddAttachmentModal";
import IconsCell from "./Cells/IconsCell";
import VerifyModal from "./Modals/VerifyModal";
import AddNoteModal from "./Modals/AddNoteModal";
import CustomToolbar from "components/CaseDossier/Toolbar";
import usePDF from "hooks/usePDF";
import { IAddressBookVerificationAttachment } from "dto/Admin/IAddressBookVerificationAttachment";
import { ItemKind } from "dto/IKindItems";
import PDFViewer from "components/PDFViewer";
import { useTranslation } from "react-i18next";

const AddressBookVerificationsGrid: FC<Props> = ({ searchText }) => {
  const { t } = useTranslation();
  const { blob, removeBlob, attachment, decryptPackageAndSetBlob } = usePDF();

  const [downloadFileName, setDownloadFileName] = useState<string>(null);

  const { data: statusesData } = statusService.useGetStatuses();

  const statuses = useMemo(() => statusesData?.data ?? [], [statusesData]);
  const { verifications, setVerifications } = useContext(
    AddressBookVerificationsContext,
  );
  const wrapperRef = useRef();

  const [addAttachmentElement, setAddAttachmentElement] =
    useState<IAddressBookVerificationListItem>(null);
  const [addNoteElement, setAddNoteElement] =
    useState<IAddressBookVerificationListItem>(null);

  const [verifyElement, setVerifyElement] =
    useState<IAddressBookVerificationListItem>(null);

  useEffect(() => {
    if (!blob || !downloadFileName) return;
    const URL = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = URL;
    link.setAttribute("download", downloadFileName);
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(URL);
    removeBlob();
  }, [blob, downloadFileName]);

  const { data, fetchNextPage, isLoading, refetch } = useInfiniteQuery(
    ["addressBookVerifications", searchText],
    async ({ pageParam = "" }) => {
      const query = new URLSearchParams(window.location.search);

      query.append("Page", pageParam !== "" ? pageParam : "0");

      if (searchText.length) {
        query.append("Text", searchText);
      }

      const res = await axiosInstance.get(
        `/admin/addressbookverifications?${query}`,
      );
      return res.data;
    },
    {
      enabled: false,
      staleTime: 0,
      cacheTime: 0,
      getNextPageParam: (lastPage) => {
        if (!lastPage.LastPage) {
          return lastPage.Page + 1;
        }
        return undefined;
      },
    },
  );

  const [isInitialized, setIsInitialized] = useState(false);

  const compileData = async () => {
    if (!data) return;

    const packs = await Promise.all(
      data?.pages
        .map((page) => {
          return page.Items;
        })
        .flat(),
    );

    setVerifications(packs.flat());
    setIsInitialized(true);
  };

  const handleVerify = (element: IAddressBookVerificationListItem) => {
    setVerifyElement(element);
  };

  const handleAddAttachment = (element: IAddressBookVerificationListItem) => {
    setAddAttachmentElement(element);
  };

  const handleAddNote = (element: IAddressBookVerificationListItem) => {
    setAddNoteElement(element);
  };

  const pdfRef = useRef<HTMLDivElement>();

  useEffect(() => {
    if (pdfRef.current) {
      pdfRef.current.classList.add(styles.show);
    }
  }, [pdfRef.current]);

  const handlePreview = (
    element: IAddressBookVerificationListItem,
    attachment: IAddressBookVerificationAttachment,
  ) => {
    setDownloadFileName(null);
    decryptPackageAndSetBlob({
      FileName: attachment.FileName,
      EncryptionKeyId: null,
      Uri: attachment.Uri,
      Kind: ItemKind.Email,
    });
  };

  const handleDownload = (
    element: IAddressBookVerificationListItem,
    attachment: IAddressBookVerificationAttachment,
  ) => {
    setDownloadFileName(attachment.FileName);
    decryptPackageAndSetBlob({
      FileName: attachment.FileName,
      EncryptionKeyId: null,
      Uri: attachment.Uri,
      Kind: ItemKind.Email,
    });
  };

  useEffect(() => {
    compileData();
  }, [data]);

  useEffect(() => {
    setVerifications([]);
    setIsInitialized(false);

    setTimeout(() => {
      refetch();
    }, 500);
  }, []);

  const reload = () => {
    setVerifications([]);
    setIsInitialized(false);

    setTimeout(() => {
      refetch();
    }, 500);
  };

  const gridRows = legalPersons.map((c) => {
    //@ts-ignore
    c.id = c.Id;
    return c;
  });
  rowsRef.current = gridRows;

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <div className={styles.list}>
        {verifications.length === 0 && !isLoading && isInitialized && (
          <div className={styles.empty}>
            <img src={Search} />
            <div>{t("emptyStates.noResults")}</div>
          </div>
        )}

        {(verifications?.length > 0 || isLoading || !isInitialized) && (
          <DataGridPremium
            sx={{
              border: 0,
              "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
                outline: "none !important",
              },
              "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
                {
                  outline: "none !important",
                },
            }}
            localeText={plGridLocales}
            slots={{ toolbar: CustomToolbar }}
            showToolbar
            slotProps={{
              loadingOverlay: {
                variant: "skeleton",
                noRowsVariant: "skeleton",
              },
            }}
            loading={!isInitialized || isLoading}
            columnHeaderHeight={30}
            onRowsScrollEnd={() => {
              fetchNextPage();
            }}
            columns={[
              {
                field: "CompanyName",
                minWidth: 140,
                flex: 1,
                headerName: t("forms.companyName"),
                cellClassName: `${styles.senderCell}`,
                valueGetter: (value, row) => row?.CompanyName,
                renderCell: (
                  params: GridRenderCellParams<
                    IAddressBookVerificationListItem,
                    string
                  >,
                ) =>
                  params.rowNode.type !== "group" ? (
                    <NameCell element={params.row}></NameCell>
                  ) : (
                    <div>{params.value}</div>
                  ),
              },
              {
                field: "Status",
                minWidth: 160,
                headerName: t("common.status"),
                renderCell: (
                  params: GridRenderCellParams<
                    IAddressBookVerificationListItem,
                    string
                  >,
                ) => (
                  <StatusCell
                    element={params.row}
                    statuses={statuses}
                  ></StatusCell>
                ),
              },

              {
                field: "Krs",
                minWidth: 120,
                headerName: "KRS",
                cellClassName: `${styles.nameCell}`,
              },
              {
                field: "Nip",
                minWidth: 120,
                headerName: "NIP",
                cellClassName: `${styles.nameCell}`,
              },

              {
                field: "CreationDate",
                width: 140,
                headerName: t("columns.date"),
                valueFormatter: (v) => {
                  if (v) {
                    return moment(v).format("HH:mm | DD.MM.yyyy");
                  }
                  return null;
                },
              },

              {
                field: "ApprovedBy",
                minWidth: 120,
                headerName: t("columns.approvedBy"),
                cellClassName: `${styles.nameCell}`,
              },

              {
                field: "DecisionDate",
                minWidth: 140,
                headerName: t("columns.accepted"),
                valueFormatter: (v) => {
                  if (v) {
                    return moment(v).format("HH:mm | DD.MM.yyyy");
                  }
                  return null;
                },
                cellClassName: `${styles.nameCell}`,
              },

              {
                field: "icons",
                width: 80,
                headerName: "icons",
                disableReorder: true,
                sortable: false,
                resizable: false,
                hideable: false,
                renderHeader: () => <div></div>,
                renderCell: (
                  params: GridRenderCellParams<
                    IAddressBookVerificationListItem,
                    string
                  >,
                ) => (
                  <IconsCell
                    onPreview={(item, attachment) => {
                      handlePreview(item, attachment);
                    }}
                    onDownload={(item, attachment) => {
                      handleDownload(item, attachment);
                    }}
                    element={params.row}
                  ></IconsCell>
                ),
              },

              {
                field: "menu",
                width: 40,
                headerName: "Menu",
                disableReorder: true,
                sortable: false,
                resizable: false,
                hideable: false,
                renderHeader: () => <div></div>,
                renderCell: (
                  params: GridRenderCellParams<
                    IAddressBookVerificationListItem,
                    string
                  >,
                ) => (
                  <MenuCell
                    element={params.row}
                    scrollWrapper={wrapperRef}
                    onVerify={handleVerify}
                    onAddAttachment={handleAddAttachment}
                    onAddNote={handleAddNote}
                  ></MenuCell>
                ),
              },
            ]}
            hideFooter={true}
            rows={verifications.map((c) => {
              //@ts-ignore
              c.id = c.Id;
              return c;
            })}
          />
        )}
      </div>

      {addAttachmentElement && (
        <div className={styles.modalBg}>
          <AddAttachmentModal
            addressBookVerificationId={addAttachmentElement.Id}
            toggle={() => setAddAttachmentElement(null)}
            reload={() => reload()}
          ></AddAttachmentModal>
        </div>
      )}

      {verifyElement && (
        <div className={styles.modalBg}>
          <VerifyModal
            addressBookVerificationId={verifyElement.Id}
            toggle={() => setVerifyElement(null)}
            reload={() => reload()}
          ></VerifyModal>
        </div>
      )}

      {addNoteElement && (
        <div className={styles.modalBg}>
          <AddNoteModal
            addressBookVerificationId={addNoteElement.Id}
            toggle={() => setAddNoteElement(null)}
            refetch={() => reload()}
          ></AddNoteModal>
        </div>
      )}

      {blob && !downloadFileName && (
        <div className={styles.PDFWrapper} ref={pdfRef}>
          <PDFViewer
            showTitle
            file={blob}
            title={
              attachment?.Label ? attachment.Label : attachment?.FileName ?? ""
            }
            showHeader={true}
            onCloseButton={() => {
              pdfRef.current.classList.remove(styles.show);
              pdfRef.current.classList.add(styles.hide);
              setTimeout(() => {
                removeBlob();
              }, 1000);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default AddressBookVerificationsGrid;
